import { useState, useEffect, useCallback, useMemo } from "react";
import * as service from "./exchangeRateService";

/**
 * Hook React centralizado de tipos de cambio.
 * Wrapper delgado sobre exchangeRateService.
 *
 * API compatible con el antiguo ER/hooks/useExchangeRates para facilitar migración.
 *
 * @param {string} pais - "Chile" o "Perú"
 * @param {number} [selectedYear] - Año para cálculos mensuales (default: año actual)
 */
export const useExchangeRates = (pais, selectedYear = new Date().getFullYear()) => {
  const [loaded, setLoaded] = useState(service.isLoaded());

  useEffect(() => {
    if (service.isLoaded()) {
      setLoaded(true);
      return;
    }
    service.loadExchangeRates().then(() => setLoaded(true));
  }, []);

  // ─── Mapas crudos (compatibilidad con ER) ────────────────────

  const dolarMaps = useMemo(
    () =>
      loaded
        ? {
            actual: service.getDolarMap(pais),
            chile: service.getDolarMap("Chile"),
            peru: service.getDolarMap("Perú"),
          }
        : {},
    [loaded, pais]
  );

  const ufMaps = useMemo(() => (loaded ? { actual: service.getUFMap() } : {}), [loaded]);

  const dolarOtroPais = useMemo(() => {
    if (!loaded) return {};
    const otro = pais === "Chile" ? "Perú" : "Chile";
    return service.getDolarMap(otro);
  }, [loaded, pais]);

  // ─── Fallback dinámico ───────────────────────────────────────

  const dolarFallback = useMemo(
    () => ({
      Chile: loaded ? service.getDolarFallback("Chile") : 0,
      Perú: loaded ? service.getDolarFallback("Perú") : 0,
    }),
    [loaded]
  );

  // ─── Funciones de consulta ───────────────────────────────────

  const getDolarDia10 = useCallback(
    (monthIndex, dolarMap, fallback) => {
      // Compatibilidad: ER pasa (monthIndex, dolarMap, fallback)
      // pero el service solo necesita (monthIndex, pais, year)
      if (dolarMap && typeof dolarMap === "object") {
        // Llamada legacy: determinar país por el mapa pasado
        let paisLookup = pais;
        if (dolarMap === dolarMaps.chile) paisLookup = "Chile";
        else if (dolarMap === dolarMaps.peru) paisLookup = "Perú";
        return service.getDolarDia10(monthIndex, paisLookup, selectedYear);
      }
      return service.getDolarDia10(monthIndex, pais, selectedYear);
    },
    [pais, selectedYear, dolarMaps.chile, dolarMaps.peru]
  );

  const getUFValue = useCallback((fechaStr) => service.getUFValue(fechaStr), []);

  const getDolarValue = useCallback((fechaStr) => service.getDolarValue(fechaStr, pais), [pais]);

  const getDolarValueChile = useCallback((fechaStr) => service.getDolarValue(fechaStr, "Chile"), []);

  const getDolarValuePeru = useCallback((fechaStr) => service.getDolarValue(fechaStr, "Perú"), []);

  // Compatibilidad: ER llama calcularDolarMensual(dolarMap, fallback, yearOverride)
  const calcularDolarMensual = useCallback(
    (dolarMap, fallback, yearOverride) => {
      const year = yearOverride || selectedYear;
      // Determinar país a partir del mapa pasado
      let paisLookup = pais;
      if (dolarMap === dolarMaps.chile) paisLookup = "Chile";
      else if (dolarMap === dolarMaps.peru) paisLookup = "Perú";
      return service.calcularDolarMensual(paisLookup, year);
    },
    [pais, selectedYear, dolarMaps.chile, dolarMaps.peru]
  );

  // ─── Valores mensuales derivados ─────────────────────────────

  const dolarMensual = useMemo(
    () => (loaded ? service.calcularDolarMensual(pais, selectedYear) : Array(12).fill(0)),
    [loaded, pais, selectedYear]
  );

  const dolarMensualAnoAnterior = useMemo(
    () => (loaded ? service.calcularDolarMensual(pais, selectedYear - 1) : Array(12).fill(0)),
    [loaded, pais, selectedYear]
  );

  const dolarMensualChile = useMemo(
    () => (loaded ? service.calcularDolarMensual("Chile", selectedYear) : Array(12).fill(0)),
    [loaded, selectedYear]
  );

  const dolarMensualPeru = useMemo(
    () => (loaded ? service.calcularDolarMensual("Perú", selectedYear) : Array(12).fill(0)),
    [loaded, selectedYear]
  );

  const dolarMensualOtroPais = useMemo(() => {
    if (!loaded) return Array(12).fill(0);
    const otro = pais === "Chile" ? "Perú" : "Chile";
    return service.calcularDolarMensual(otro, selectedYear);
  }, [loaded, pais, selectedYear]);

  // PASO 3 (Criterio 1), ajuste de Cowork (2026-10-06): TC PROMEDIO real del mes (Criterio 1b,
  // `calcularDolarMensualPromedio`) — distinto de `dolarMensual` (arriba, "último valor
  // conocido del mes", el criterio de HOY fuera de Fase 3). Mismo gating por `loaded` que el
  // resto de los `useMemo` de este hook: un consumidor que llamara a
  // `service.calcularDolarMensualPromedio` directo, fuera de este hook, con sus propios deps
  // `[pais, selectedYear]` (sin `loaded`), congelaría el array vacío/último-disponible del
  // primer render si la cache del TC todavía no cargó, y nunca lo recalcularía después (el
  // `useMemo` no vuelve a correr si sus deps no cambian) — bug real encontrado en este punto.
  const dolarMensualPromedio = useMemo(
    () => (loaded ? service.calcularDolarMensualPromedio(pais, selectedYear) : Array(12).fill(0)),
    [loaded, pais, selectedYear]
  );

  const dolarMensualPromedioAnoAnterior = useMemo(
    () => (loaded ? service.calcularDolarMensualPromedio(pais, selectedYear - 1) : Array(12).fill(0)),
    [loaded, pais, selectedYear]
  );

  const dolarMensualPromedioChile = useMemo(
    () => (loaded ? service.calcularDolarMensualPromedio("Chile", selectedYear) : Array(12).fill(0)),
    [loaded, selectedYear]
  );

  const dolarMensualPromedioPeru = useMemo(
    () => (loaded ? service.calcularDolarMensualPromedio("Perú", selectedYear) : Array(12).fill(0)),
    [loaded, selectedYear]
  );

  // ─── Cross-rate y tipo de cambio actual ──────────────────────

  const crossRate = useMemo(() => (loaded ? service.getCrossRate() : 0), [loaded]);

  const tipoCambioActual = useMemo(() => (loaded ? service.getDolarFallback(pais) : 0), [loaded, pais]);

  // Solo no-null en el dev server real (localhost, `npm start`) — ver exchangeRateService.js.
  // null en producción y en test: nunca se muestra un aviso ahí.
  const backup = useMemo(() => (loaded ? service.getBackupStatus() : null), [loaded]);

  return {
    loaded,
    // Maps crudos (compatibilidad ER)
    dolarMaps,
    ufMaps,
    dolarOtroPais,
    // Fallback dinámico
    dolarFallback,
    // Funciones de consulta
    getDolarDia10,
    getUFValue,
    getDolarValue,
    getDolarValueChile,
    getDolarValuePeru,
    calcularDolarMensual,
    // Valores mensuales derivados
    dolarMensual,
    dolarMensualAnoAnterior,
    dolarMensualChile,
    dolarMensualPeru,
    dolarMensualOtroPais,
    // TC PROMEDIO mensual (Criterio 1b, Fase 3) — distinto de dolarMensual* arriba
    dolarMensualPromedio,
    dolarMensualPromedioAnoAnterior,
    dolarMensualPromedioChile,
    dolarMensualPromedioPeru,
    // Extras (VP, Categorías)
    crossRate,
    tipoCambioActual,
    // Aviso de respaldo (solo dev server real)
    backup,
  };
};
